/**
 * AMOLED Theme Enhancement Utility
 * 
 * This utility provides dynamic AMOLED theme enhancements for complex
 * Tailwind class combinations that can't be handled with CSS alone.
 */

export class AMOLEDEnhancer {
  constructor() {
    this.observer = null
    this.isEnabled = false
    this.enhancedElements = new WeakSet()
    this.appliedStyles = new WeakMap()
    this.eventHandlers = new WeakMap() // Store event handlers for safe removal
  }

  /**
   * Enable AMOLED enhancements
   */
  enable() {
    if (this.isEnabled) return
    
    this.isEnabled = true
    this.enhanceExistingElements()
    this.startObserving()
    
    console.log('🖤 AMOLED Enhancer: Enabled')
  }

  /**
   * Disable AMOLED enhancements
   */
  disable() {
    if (!this.isEnabled) return
    
    this.isEnabled = false
    this.stopObserving()
    this.cleanupAllEnhancements()
    
    console.log('🖤 AMOLED Enhancer: Disabled')
  }

  /**
   * Enhance existing elements on the page
   */
  enhanceExistingElements() {
    const elements = document.querySelectorAll('*[class]')
    elements.forEach(element => this.enhanceElement(element))
  }

  /**
   * Start observing DOM changes to enhance new elements
   */
  startObserving() {
    if (this.observer) this.stopObserving()
    
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.enhanceElement(node)
            
            // Also enhance child elements
            const childElements = node.querySelectorAll('*[class]')
            childElements.forEach(element => this.enhanceElement(element))
          }
        })
      })
    })
    
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    })
  }

  /**
   * Stop observing DOM changes
   */
  stopObserving() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  /**
   * Enhance a single element for AMOLED mode
   */
  enhanceElement(element) {
    if (!element.classList || !this.isEnabled || !this.isAMOLEDActive()) return
    
    const classList = Array.from(element.classList)
    
    // Only apply minimal enhancements for complex patterns that CSS can't handle
    this.fixOpacityBackgrounds(element, classList)
    this.fixFileInputs(element)
    this.fixNotesListHover(element, classList)
    this.fixTabScrollArrowsConservative(element, classList)
    this.fixFolderTabScrollbar(element, classList)
  }

  /**
   * Fix opacity-based dark backgrounds - only for complex patterns
   */
  fixOpacityBackgrounds(element, classList) {
    // Only handle complex opacity patterns that CSS can't easily target
    const opacityBgPatterns = [
      /dark:bg-(\w+)-900\/(\d+)/
    ]
    
    opacityBgPatterns.forEach(pattern => {
      classList.forEach(className => {
        if (pattern.test(className)) {
          const match = className.match(pattern)
          if (match) {
            // Apply AMOLED-friendly background for opacity patterns only
            this.setTrackedStyle(element, 'background-color', 'rgba(10, 10, 10, 0.8)', 'important')
          }
        }
      })
    })
  }

  /**
   * Fix backdrop blur combinations
   */
  fixBackdropBlur(element, classList) {
    const hasBackdrop = classList.some(c => c.includes('backdrop-blur'))
    const hasDarkBg = classList.some(c => c.includes('dark:bg-') && c.includes('/'))
    
    if (hasBackdrop && hasDarkBg) {
      element.style.setProperty('backdrop-filter', 'blur(8px) brightness(0.1)', 'important')
      element.style.setProperty('background-color', 'rgba(0, 0, 0, 0.9)', 'important')
    }
  }

  /**
   * Fix gradient combinations
   */
  fixGradients(element, classList) {
    const hasGradient = classList.some(c => c.includes('bg-gradient-to-'))
    const hasLightFrom = classList.some(c => c.includes('from-') && (c.includes('-50') || c.includes('-100')))
    const hasLightTo = classList.some(c => c.includes('to-') && (c.includes('-50') || c.includes('-100')))
    
    if (hasGradient && (hasLightFrom || hasLightTo)) {
      element.style.setProperty('background-image', 'linear-gradient(to bottom right, #0a0a0a, #171717)', 'important')
    }
  }

  /**
   * Fix overlay patterns
   */
  fixOverlays(element, classList) {
    const hasBgWhite = classList.some(c => c.includes('bg-white/'))
    const hasBgBlack = classList.some(c => c.includes('bg-black') && c.includes('bg-opacity-'))
    
    if (hasBgWhite) {
      element.style.setProperty('background-color', 'rgba(10, 10, 10, 0.9)', 'important')
    }
    
    if (hasBgBlack) {
      element.style.setProperty('background-color', 'rgba(0, 0, 0, 0.95)', 'important')
    }
  }

  /**
   * Fix file input styling
   */
  fixFileInputs(element) {
    if (element.tagName === 'INPUT' && element.type === 'file') {
      // Fix file selector button
      const style = document.createElement('style')
      style.textContent = `
        .amoled input[type="file"]::-webkit-file-upload-button {
          background-color: #171717 !important;
          color: #f5f5f5 !important;
          border-color: #262626 !important;
        }
      `
      
      if (!document.head.querySelector('style[data-amoled-file-fix]')) {
        style.setAttribute('data-amoled-file-fix', 'true')
        document.head.appendChild(style)
      }
    }
  }

  /**
   * Fix notes list hover patterns
   */
  fixNotesListHover(element, classList) {
    // Target notes list items with complex hover combinations
    const hasGroup = classList.includes('group')
    const hasBorderB = classList.some(c => c.includes('border-b'))
    const hasHoverBg = classList.some(c => c.includes('hover:bg-gray') || c.includes('dark:hover:bg-gray'))
    
    if (hasGroup && (hasBorderB || hasHoverBg)) {
      // Store event handlers for cleanup
      const mouseEnterHandler = () => {
        this.setTrackedStyle(element, 'background-color', '#0f0f0f', 'important')
        this.setTrackedStyle(element, 'border-color', '#262626', 'important')
        
        // Find and enhance title text on hover
        const title = element.querySelector('h3')
        if (title) {
          this.setTrackedStyle(title, 'color', '#a855f7', 'important')
        }
      }
      
      const mouseLeaveHandler = () => {
        element.style.removeProperty('background-color')
        element.style.removeProperty('border-color')
        
        // Reset title color
        const title = element.querySelector('h3')
        if (title) {
          title.style.removeProperty('color')
        }
      }
      
      // Add enhanced hover listeners for better AMOLED experience (tracked for cleanup)
      this.addTrackedEventListener(element, 'mouseenter', mouseEnterHandler)
      this.addTrackedEventListener(element, 'mouseleave', mouseLeaveHandler)
    }
  }

  /**
   * Fix tab scroll arrows for better AMOLED contrast - conservative approach
   */
  fixTabScrollArrowsConservative(element, classList) {
    const hasScrollArrow = element.tagName === 'BUTTON' && 
                          (element.getAttribute('aria-label') === 'Scroll left' || 
                           element.getAttribute('aria-label') === 'Scroll right')
    
    if (hasScrollArrow) {
      const ariaLabel = element.getAttribute('aria-label')
      const chevronIcon = element.querySelector('svg')
      
      // Fix icon color
      if (chevronIcon) {
        this.setTrackedStyle(chevronIcon, 'color', '#737373', 'important')
      }
      
      if (ariaLabel === 'Scroll right') {
        this.setTrackedStyle(element, 'background', 'linear-gradient(to left, #0a0a0a, transparent)', 'important')
        
        const mouseEnterHandler = () => {
          this.setTrackedStyle(element, 'background', 'linear-gradient(to left, #171717, transparent)', 'important')
          if (chevronIcon) {
            this.setTrackedStyle(chevronIcon, 'color', '#a3a3a3', 'important')
          }
        }
        
        const mouseLeaveHandler = () => {
          this.setTrackedStyle(element, 'background', 'linear-gradient(to left, #0a0a0a, transparent)', 'important')
          if (chevronIcon) {
            this.setTrackedStyle(chevronIcon, 'color', '#737373', 'important')
          }
        }
        
        this.addTrackedEventListener(element, 'mouseenter', mouseEnterHandler)
        this.addTrackedEventListener(element, 'mouseleave', mouseLeaveHandler)
        
      } else if (ariaLabel === 'Scroll left') {
        this.setTrackedStyle(element, 'background', 'linear-gradient(to right, #0a0a0a, transparent)', 'important')
        
        const mouseEnterHandler = () => {
          this.setTrackedStyle(element, 'background', 'linear-gradient(to right, #171717, transparent)', 'important')
          if (chevronIcon) {
            this.setTrackedStyle(chevronIcon, 'color', '#a3a3a3', 'important')
          }
        }
        
        const mouseLeaveHandler = () => {
          this.setTrackedStyle(element, 'background', 'linear-gradient(to right, #0a0a0a, transparent)', 'important')
          if (chevronIcon) {
            this.setTrackedStyle(chevronIcon, 'color', '#737373', 'important')
          }
        }
        
        this.addTrackedEventListener(element, 'mouseenter', mouseEnterHandler)
        this.addTrackedEventListener(element, 'mouseleave', mouseLeaveHandler)
      }
    }
  }

  /**
   * Fix dynamic status colors
   */
  fixStatusColors(element, classList) {
    const colorMappings = {
      'text-green-800': '#22c55e',
      'text-red-800': '#ef4444',
      'text-yellow-800': '#eab308',
      'text-blue-800': '#3b82f6',
      'text-amber-800': '#f59e0b',
      'text-green-200': '#22c55e',
      'text-red-200': '#ef4444',
      'text-yellow-200': '#eab308',
      'text-blue-200': '#3b82f6',
      'text-amber-200': '#f59e0b'
    }
    
    Object.entries(colorMappings).forEach(([className, color]) => {
      if (classList.includes(className)) {
        element.style.setProperty('color', color, 'important')
      }
    })
  }

  /**
   * Force refresh all enhancements
   */
  refresh() {
    if (this.isEnabled) {
      this.enhanceExistingElements()
    }
  }

  /**
   * Check if AMOLED mode is currently active
   */
  isAMOLEDActive() {
    return document.documentElement.classList.contains('amoled')
  }

  /**
   * Cleanup all AMOLED enhancements when switching themes
   */
  cleanupAllEnhancements() {
    // Remove all inline styles applied by the enhancer
    const allElements = document.querySelectorAll('*')
    allElements.forEach(element => {
      if (this.enhancedElements.has(element)) {
        // Remove inline styles
        const appliedStyles = this.appliedStyles.get(element)
        if (appliedStyles) {
          appliedStyles.forEach(property => {
            element.style.removeProperty(property)
          })
        }
        
        // Remove specific event handlers we added (React-safe)
        const handlers = this.eventHandlers.get(element)
        if (handlers) {
          handlers.forEach(({ event, handler }) => {
            element.removeEventListener(event, handler)
          })
        }
      }
    })
    
    // Clear tracking
    this.enhancedElements = new WeakSet()
    this.appliedStyles = new WeakMap()
    this.eventHandlers = new WeakMap()
    
    // Remove any dynamically added styles
    const amoledFileFixStyle = document.head.querySelector('style[data-amoled-file-fix]')
    if (amoledFileFixStyle) {
      amoledFileFixStyle.remove()
    }
    
    console.log('🖤 AMOLED Enhancer: All enhancements cleaned up (React-safe)')
  }

  /**
   * Fix folder tab scrollbar visibility in AMOLED mode
   */
  fixFolderTabScrollbar(element, classList) {
    // Target folder tab scrollable container: has 'scrollbar-hide' class and specific layout classes
    const hasScrollbarHide = classList.includes('scrollbar-hide')
    const hasOverflowXAuto = classList.includes('overflow-x-auto')
    const hasFlex = classList.includes('flex')
    const hasGap1 = classList.includes('gap-1')
    
    if (hasScrollbarHide && hasOverflowXAuto && hasFlex && hasGap1) {
      // This is likely the folder tab scrollable container - override inline styles
      this.setTrackedStyle(element, 'scrollbar-width', 'thin', 'important')
      this.setTrackedStyle(element, '-ms-overflow-style', 'auto', 'important')
      this.setTrackedStyle(element, 'scrollbar-color', 'rgba(64, 64, 64, 0.6) transparent', 'important')
      
      console.log('🖤 AMOLED Enhancer: Folder tab scrollbar visibility restored')
    }
  }

  /**
   * Track inline style application for cleanup
   */
  setTrackedStyle(element, property, value, priority = '') {
    element.style.setProperty(property, value, priority)
    
    // Track this element and the style property
    this.enhancedElements.add(element)
    if (!this.appliedStyles.has(element)) {
      this.appliedStyles.set(element, new Set())
    }
    this.appliedStyles.get(element).add(property)
  }

  /**
   * Add tracked event listener for safe cleanup
   */
  addTrackedEventListener(element, event, handler) {
    element.addEventListener(event, handler)
    
    // Track this element and event handler
    this.enhancedElements.add(element)
    if (!this.eventHandlers.has(element)) {
      this.eventHandlers.set(element, [])
    }
    this.eventHandlers.get(element).push({ event, handler })
  }
}

// Create singleton instance
export const amoledEnhancer = new AMOLEDEnhancer()

/**
 * Initialize AMOLED enhancer based on current theme
 */
export const initializeAMOLEDEnhancer = () => {
  // Check theme on load
  const checkAndUpdate = () => {
    if (amoledEnhancer.isAMOLEDActive()) {
      amoledEnhancer.enable()
    } else {
      amoledEnhancer.disable()
    }
  }
  
  // Initial check
  checkAndUpdate()
  
  // Watch for theme changes
  const observer = new MutationObserver(() => {
    checkAndUpdate()
  })
  
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class']
  })
  
  // Also listen for storage changes (theme persistence)
  window.addEventListener('storage', (e) => {
    if (e.key === 'theme') {
      setTimeout(checkAndUpdate, 100) // Small delay to let theme change apply
    }
  })
  
  console.log('🖤 AMOLED Enhancer: Initialized')
}

export default amoledEnhancer 